import { useQueryClient } from '@tanstack/react-query';
import { getRepositoryQueryKey, OrderItemClaim } from '@mews-commander/data-access';
import { HocSubmitHandler } from '@mews-commander/form';
import { getOrderItemUnclaimedCount } from '@mews-commander/legacy-entities/src/ducks/orderItemNew';
import { useAddProductOrderMutation } from '../../../../mutations/addProductOrderMutation';
import { useClaimOrderItemsMutation } from '../../../../mutations/claimOrderItemsMutation';
import { AddProductsFormValues } from '../AddProductsForm';

interface Result {
    loading: boolean;
    addProductsToReservation: HocSubmitHandler<AddProductsFormValues>;
}

export const useAddProductsToReservation = (reservationId: string | undefined, onClose: () => void): Result => {
    const addProductOrderMutation = useAddProductOrderMutation();
    const claimOrderItemsMutation = useClaimOrderItemsMutation();
    const queryClient = useQueryClient();

    return {
        loading: addProductOrderMutation.isPending,
        addProductsToReservation: (
            { productId, quantity, consumedUtc, claimForToday }: AddProductsFormValues,
            { reset }
        ) => {
            if (!reservationId || !productId) {
                return;
            }

            addProductOrderMutation.mutate(
                {
                    productOrders: [{ reservationId, productId, count: quantity, consumedUtc }],
                },
                {
                    onSuccess: async ({ productOrders }) => {
                        reset();

                        if (claimForToday && productOrders?.length) {
                            const orderItemClaims: OrderItemClaim[] = productOrders.map((orderItem) => ({
                                orderItemId: orderItem.id,
                                claimedCount: getOrderItemUnclaimedCount(orderItem),
                            }));
                            claimOrderItemsMutation.mutate({ orderItemClaims }, { onSuccess: onClose });
                        } else {
                            await queryClient.invalidateQueries({
                                queryKey: getRepositoryQueryKey('productChecklist'),
                            });
                            onClose();
                        }
                    },
                }
            );
        },
    };
};
